import { apiClient } from './api'
import type {
  AvailabilityResponse,
  PortalAppointment,
  PortalDashboard,
  PortalInvoice,
  PortalPet,
  TrackPayload,
} from '../types'

// ─── Dashboard ────────────────────────────────────────────────────────────────

export async function getDashboard(): Promise<PortalDashboard> {
  const { data } = await apiClient.get<PortalDashboard>('/portal/dashboard')
  return data
}

// ─── Pets ─────────────────────────────────────────────────────────────────────

export async function listPets(): Promise<PortalPet[]> {
  const { data } = await apiClient.get<PortalPet[]>('/portal/pets')
  return data
}

export async function createPet(payload: Partial<PortalPet>): Promise<PortalPet> {
  const { data } = await apiClient.post<PortalPet>('/portal/pets', { pet: payload })
  return data
}

export async function updatePet(id: number, payload: Partial<PortalPet>): Promise<PortalPet> {
  const { data } = await apiClient.patch<PortalPet>(`/portal/pets/${id}`, { pet: payload })
  return data
}

export async function deletePet(id: number): Promise<void> {
  await apiClient.delete(`/portal/pets/${id}`)
}

// ─── Appointments ─────────────────────────────────────────────────────────────

export async function listAppointments(scope?: 'upcoming' | 'past'): Promise<PortalAppointment[]> {
  const { data } = await apiClient.get<PortalAppointment[]>('/portal/appointments', {
    params: scope ? { scope } : undefined,
  })
  return data
}

export async function getAppointment(id: number): Promise<PortalAppointment> {
  const { data } = await apiClient.get<PortalAppointment>(`/portal/appointments/${id}`)
  return data
}

export async function createAppointment(payload: {
  service_id: number
  pet_id: number
  veterinarian_id?: number
  scheduled_at: string
  address_street: string
  address_city: string
  address_state: string
  address_zip: string
  notes?: string
}): Promise<PortalAppointment> {
  const { data } = await apiClient.post<PortalAppointment>('/portal/appointments', { appointment: payload })
  return data
}

export async function cancelAppointment(id: number): Promise<void> {
  await apiClient.delete(`/portal/appointments/${id}`)
}

// ─── Invoices ─────────────────────────────────────────────────────────────────

export async function listInvoices(scope?: 'outstanding' | 'paid'): Promise<PortalInvoice[]> {
  const { data } = await apiClient.get<PortalInvoice[]>('/portal/invoices', {
    params: scope ? { scope } : undefined,
  })
  return data
}

export async function getInvoice(id: number): Promise<PortalInvoice> {
  const { data } = await apiClient.get<PortalInvoice>(`/portal/invoices/${id}`)
  return data
}

export async function payInvoice(id: number): Promise<{
  checkout_url: string
  stripe_invoice_id: string
  provider_status: { mode: string; configured: boolean; message: string }
}> {
  const { data } = await apiClient.post(`/portal/invoices/${id}/pay`, {})
  return data
}

// ─── Availability ─────────────────────────────────────────────────────────────

export async function getAvailability(params: {
  date: string
  service_id?: number
  veterinarian_id?: number
  market_id?: number
}): Promise<AvailabilityResponse> {
  const { data } = await apiClient.get<AvailabilityResponse>('/portal/availability', { params })
  return data
}

// ─── Tracking (token-authed, no JWT needed) ──────────────────────────────────

export async function getTrack(token: string): Promise<TrackPayload> {
  // Fresh axios call so we don't accidentally send a stale customer JWT.
  const { data } = await apiClient.get<TrackPayload>(`/portal/track/${token}`)
  return data
}
